import { formatNumberByLocale, type LocaleInput } from '@/utils/localeFormatters';

export type PlotExtents = {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
};

export type PlotLayout = {
    width: number;
    height: number;
    padL: number;
    padR: number;
    padT: number;
    padB: number;
};

export type PlotPoint = {
    x: number;
    y: number;
};

export const PEN_PLOT_VIEW: PlotLayout = {
    width: 800,
    height: 420,
    padL: 64,
    padR: 28,
    padT: 16,
    padB: 48,
};

const MIN_ASPECT = 2;
const MAX_ASPECT = 3;
const BASE_PLOT_HEIGHT = 420;
const PLOT_HEIGHT_SCALE = 0.78;

export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/** Landscape viewBox from pen distance vs depth, floored at 2:1, then shortened. */
export function plotLayoutFromExtents(extents: PlotExtents): PlotLayout {
    const xSpan = Math.max(0.01, extents.xMax - extents.xMin);
    const ySpan = Math.max(0.01, extents.yMax - extents.yMin);
    const ratio = clamp(xSpan / ySpan, MIN_ASPECT, MAX_ASPECT);
    return {
        width: Math.round(BASE_PLOT_HEIGHT * ratio),
        height: Math.round(BASE_PLOT_HEIGHT * PLOT_HEIGHT_SCALE),
        padL: PEN_PLOT_VIEW.padL,
        padR: PEN_PLOT_VIEW.padR,
        padT: PEN_PLOT_VIEW.padT,
        padB: PEN_PLOT_VIEW.padB,
    };
}

export function plotRect(layout: PlotLayout = PEN_PLOT_VIEW): {
    x: number;
    y: number;
    w: number;
    h: number;
} {
    return {
        x: layout.padL,
        y: layout.padT,
        w: layout.width - layout.padL - layout.padR,
        h: layout.height - layout.padT - layout.padB,
    };
}

export function extentsFromLimits(
    distanceMax: number,
    depthMax: number,
    distanceMin = 0,
    depthMin = 0
): PlotExtents {
    return {
        xMin: Math.min(0, distanceMin),
        xMax: Math.max(1, distanceMax),
        yMin: Math.min(0, depthMin),
        yMax: Math.max(1, depthMax),
    };
}

export function metersToPx(
    xM: number,
    yM: number,
    extents: PlotExtents,
    layout: PlotLayout = PEN_PLOT_VIEW
): PlotPoint {
    const rect = plotRect(layout);
    const nx =
        (clamp(xM, extents.xMin, extents.xMax) - extents.xMin) / (extents.xMax - extents.xMin);
    const ny =
        (clamp(yM, extents.yMin, extents.yMax) - extents.yMin) / (extents.yMax - extents.yMin);
    return {
        x: rect.x + nx * rect.w,
        y: rect.y + ny * rect.h,
    };
}

export function makeTicks(min: number, max: number, count = 5): number[] {
    const span = Math.max(1e-6, Math.abs(max - min));
    const raw = span / Math.max(1, count);
    const pow = 10 ** Math.floor(Math.log10(raw));
    const mult = raw / pow;
    const step = mult >= 5 ? 5 * pow : mult >= 2 ? 2 * pow : pow;
    const start = Math.ceil(min / step) * step;
    const ticks: number[] = [min];
    for (let value = start; value < max - 1e-9; value += step) {
        if (value - min > 1e-9) {
            ticks.push(Number(value.toFixed(2)));
        }
    }
    ticks.push(max);
    return ticks;
}

export function formatTick(value: number, locale: LocaleInput): string {
    const decimals = Math.abs(value - Math.round(value)) < 1e-6 ? 0 : 1;
    return formatNumberByLocale(value, locale, decimals);
}
